import React, { Component, ReactNode, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: '',
    };
  }

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      errorMessage: error?.message || 'เกิดข้อผิดพลาดในการแสดงผล',
    };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary caught error]:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public override render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center p-4 font-['Prompt',sans-serif]">
          <div className="max-w-md w-full bg-white rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-200 text-center">
            <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-rose-100 shadow-sm">
              <AlertTriangle className="w-8 h-8" />
            </div>

            <h2 className="text-xl font-bold text-slate-900 mb-2">
              พบข้อผิดพลาดในการแสดงผล
            </h2>
            <p className="text-xs text-slate-500 mb-6 leading-relaxed">
              ขออภัยในความไม่สะดวก ระบบตรวจพบข้อผิดพลาด กรุณากดปุ่มด้านล่างเพื่อโหลดหน้าใหม่หรือกลับสู่หน้าหลัก
            </p>

            {this.state.errorMessage && (
              <div className="bg-slate-50 rounded-2xl p-3 text-left mb-6 border border-slate-200/60 overflow-hidden">
                <p className="text-[11px] font-mono text-rose-600 font-semibold break-words">
                  {this.state.errorMessage}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={this.handleReload}
                className="py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl text-xs font-bold flex items-center justify-center gap-2 transition-colors cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
                <span>โหลดหน้าใหม่</span>
              </button>

              <button
                type="button"
                onClick={this.handleGoHome}
                className="py-3 px-4 bg-gradient-to-r from-[#E60067] to-[#D80056] hover:from-[#C20048] hover:to-[#B0003E] text-white rounded-2xl text-xs font-bold flex items-center justify-center gap-2 shadow-md shadow-pink-500/20 transition-all cursor-pointer"
              >
                <Home className="w-4 h-4" />
                <span>กลับหน้าหลัก</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
